import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { RateInputs, FormRateInputs } from '../types/rates';
import { rateInputSchema } from '../utils/validation';
import { validationValues, getMinimumDeferralPeriod, getFormDefaults, getMaximumDeferralPeriod } from '../config/formConfig';
import InfoTooltip from './InfoTooltip';

interface RateInputFormProps {
  onSubmit: (data: RateInputs) => Promise<boolean>;
  resetTrigger?: number;
  loading?: boolean;
  onFormChange?: () => void;
}

const RateInputForm: React.FC<RateInputFormProps> = ({ onSubmit, resetTrigger, loading = false, onFormChange }) => {
  // Get testAssumeDate from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const testAssumeDate = urlParams.get('testAssumeDate');
  
  // Get form defaults with testAssumeDate consideration
  const getInitialDefaults = () => {
    const defaults = getFormDefaults();
    if (testAssumeDate && dayjs(testAssumeDate).isValid()) {
      defaults.investmentDate = testAssumeDate;
    }
    return defaults;
  };

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormRateInputs>({
    resolver: yupResolver(rateInputSchema) as any,
    mode: 'onChange',
    defaultValues: getInitialDefaults(),
  });

  // const [agileIncomeStartAge, setAgileIncomeStartAge] = useState(0)
  const [deferralPeriodMin, setDeferralPeriodMin] = useState(1)
  const [deferralPeriodMax, setDeferralPeriodMax] = useState(20)

  const investorType = watch('investorType');
  const hasSpouse = watch('hasSpouse');
  const lifeInsuredAge = watch('lifeInsuredAge');
  const spouseAge = watch('spouseAge');
  const investmentDate = watch('investmentDate');
  // const deferralPeriod = watch('deferralPeriod');
  
  // Check if options should be disabled for existing investors with early dates
  const disableSpouseAndPension = investorType === 'existing' && 
    investmentDate && 
    dayjs(investmentDate).isBefore(validationValues.disableOptionsPriorTo);
  // On 16/01 Tim requested we revert this change. I'm leaving the logic in place, to re-implement if needed
  // const disableSpouseAndPension = false
  
  // Show agile income start age field only when required data is available
  const enableDeferralPeriod = lifeInsuredAge && investmentDate;

  // Calculate if spouse and life insured are same age
  const showYoungerSpouseField = hasSpouse && Number(spouseAge) === Number(lifeInsuredAge);

  const cleanUpNumber = (value: string, min: number = 0, max: number = 120) => {
    const cleaned = value.replace(/^0+(?=\d)/, '');
    const asNum = Number(cleaned)
    if (asNum < min || asNum > max) return ""
    return cleaned;
  };
  // Auto-calculate agile income start age based on AC11
  // useEffect(() => {
  //   setAgileIncomeStartAge(deferralPeriod + lifeInsuredAge)
  // }, [lifeInsuredAge, deferralPeriod]);

  // Reset investmentDate when switching to 'new' investor type
  useEffect(() => {
    if (investorType === 'new') {
      const newDate = testAssumeDate && dayjs(testAssumeDate).isValid() 
        ? testAssumeDate 
        : dayjs().format('YYYY-MM-DD');
      setValue('investmentDate', newDate);
    }
  }, [investorType, testAssumeDate, setValue]);

  // Disable and default hasSpouse and agePensionPlus for early existing investors
  useEffect(() => {
    if (disableSpouseAndPension) {
      setValue('hasSpouse', false);
      setValue('agePensionPlus', false);
    }
  }, [setValue, disableSpouseAndPension]);

  // Update the minimum Deferral period if the investmentDate changes
  useEffect(() => {
    setDeferralPeriodMin(getMinimumDeferralPeriod(investmentDate, investorType || 'new'))
  }, [investmentDate, investorType]);
  
  // Update the maximum Deferral period if the lifeInsuredAge changes
  useEffect(() => {
    setDeferralPeriodMax(getMaximumDeferralPeriod(lifeInsuredAge))
  }, [lifeInsuredAge]);


  // Derive deferral period from agile income start age
  // useEffect(() => {
  //   if (agileIncomeStartAge && lifeInsuredAge) {
  //     const derivedDeferralPeriod = Number(agileIncomeStartAge) - Number(lifeInsuredAge);
  //     setValue('deferralPeriod', derivedDeferralPeriod);
  //   }
  // }, [agileIncomeStartAge, lifeInsuredAge, setValue]);

  // Reset form when resetTrigger changes
  useEffect(() => {
    if (resetTrigger !== undefined) {
      reset();
    }
  }, [resetTrigger, reset]);

  // Call onFormChange when form becomes dirty
  useEffect(() => {
    if (isDirty && onFormChange) {
      onFormChange();
    }
  }, [isDirty, onFormChange]);

  // const handleAgileIncomeStartAge = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if ((import.meta as { env?: Record<string, string> }).env?.VITE_DEBUG_LOGGING === 'true') {
  //     console.log('e', e.target.value, deferralPeriod);
  //   }
  //   setValue('deferralPeriod', Number(e.target.value) - Number(lifeInsuredAge));
  // };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            AGILE Rates Centre
          </Typography>
          <Box sx={{ borderBottom: '2px solid #E5E5E5', mb: 3 }} />

          <Box component="form" onSubmit={handleSubmit(async (data) => {
            const success = await onSubmit(data as RateInputs);
            if (success) {
              // Reset dirty state on successful submission
              reset(data as FormRateInputs, { keepValues: true });
            }
          })}>
            {/* Test Date Warning */}
            {testAssumeDate && investorType === 'existing' && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                testAssumeDate is not being used as the Investor Type is set to existing
              </Alert>
            )}
            
            <Grid container spacing={3}>
              {/* Investor Type */}
              <Grid size={{ xs: 12 }}>
                <FormControl error={!!errors.investorType}>
                  <FormLabel>Are you a new or an existing AGILE investor?</FormLabel>
                  <Controller
                    name="investorType"
                    control={control}
                    render={({ field }) => (
                      <ToggleButtonGroup
                        {...field}
                        exclusive
                        onChange={(_, value) => value && field.onChange(value)}
                        sx={{ mt: 1 }}
                      >
                        <ToggleButton value="new">New</ToggleButton>
                        <ToggleButton value="existing">Existing</ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  />
                  {errors.investorType && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                      {errors.investorType.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Investment Date - Only for existing */}
              {investorType === 'existing' && (
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="investmentDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Existing AGILE investment policy commencement date"
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) => field.onChange(date?.format('YYYY-MM-DD'))}
                        minDate={dayjs(validationValues.investmentDateMin)}
                        maxDate={dayjs(validationValues.investmentDateMax)}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            error: !!errors.investmentDate,
                            helperText: errors.investmentDate?.message,
                            fullWidth: true,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Life Insured Gender */}
              <Grid size={{ xs: 12 }}>
                <FormControl error={!!errors.lifeInsuredGender}>
                  <FormLabel>Your Gender</FormLabel>
                  <Controller
                    name="lifeInsuredGender"
                    control={control}
                    render={({ field }) => (
                      <ToggleButtonGroup
                        {...field}
                        exclusive
                        onChange={(_, value) => value && field.onChange(value)}
                        sx={{ mt: 1 }}
                      >
                        <ToggleButton value="male">Male</ToggleButton>
                        <ToggleButton value="female">Female</ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  />
                  {errors.lifeInsuredGender && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                      {errors.lifeInsuredGender.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Life Insured Age */}
              <Grid size={{ xs: 12, md: 8 }}>
                <FormControl fullWidth>
                  <FormLabel>
                    {investorType === 'existing'
                      ? 'Your age at policy commencement'
                      : 'Your age'}
                  </FormLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                    <Controller
                      name="lifeInsuredAge"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          value={field.value}
                          type="text"
                          sx={{ width: 100 }}
                          size="small"
                          error={!!errors.lifeInsuredAge}
                          onChange={(e) => {
                            const cleaned = cleanUpNumber(e.target.value);
                            if (cleaned) field.onChange(cleaned ? Number(cleaned) : '');
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        />
                      )}
                    />
                    <Box sx={{ flex: 1, px: 2 }}>
                      <Controller
                        name="lifeInsuredAge"
                        control={control}
                        render={({ field }) => (
                          <Slider 
                            {...field} 
                            min={validationValues.lifeInsuredAgeMin}
                            max={validationValues.lifeInsuredAgeMax}
                            step={1} 
                            marks 
                            valueLabelDisplay="auto" 
                            slotProps={{
                              valueLabel: {
                                style: {
                                  transition: 'opacity 1s ease-in-out',
                                  opacity: 0,
                                },
                              },
                            }}
                            onChangeCommitted={() => {
                              const valueLabel = document.querySelector('.MuiSlider-valueLabel') as HTMLElement;
                              if (valueLabel) {
                                valueLabel.style.opacity = '1';
                                setTimeout(() => {
                                  valueLabel.style.opacity = '0';
                                }, 2000);
                              }
                            }}
                            onChange={(_, value) => {
                              field.onChange(value);
                              const valueLabel = document.querySelector('.MuiSlider-valueLabel') as HTMLElement;
                              if (valueLabel) {
                                valueLabel.style.opacity = '1';
                              }
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  {errors.lifeInsuredAge && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {errors.lifeInsuredAge.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Spouse Insured */}
              <Grid size={{ xs: 12 }}>
                <FormControl error={!!errors.hasSpouse} disabled={disableSpouseAndPension || false}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormLabel>Elect Spouse Insured Option?</FormLabel>
                    <InfoTooltip>
                      The 'Spouse Insured' option is available to investors upon commencing their AGILE income. Election of this option will mean that upon death of the Investor, the Lifetime Income continues to be paid for the lifetime of any Surviving Spouse. Election of this option may vary the Lifetime Income Rates used to calculate the Lifetime income amount. If this option is elected here, the user must provide age and gender of the nominated spouse. Election is irrevocable. Refer to PDS for more information.
                    </InfoTooltip>
                  </Box>
                  <Controller
                    name="hasSpouse"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        {...field}
                        row
                        value={field.value?.toString() || ''}
                        onChange={(e) => field.onChange(e.target.value === 'true')}
                        sx={{ mt: 1 }}
                      >
                        <FormControlLabel value="true" control={<Radio />} label="Yes" disabled={disableSpouseAndPension || false} />
                        <FormControlLabel value="false" control={<Radio />} label="No" disabled={disableSpouseAndPension || false} />
                      </RadioGroup>
                    )}
                  />
                  {errors.hasSpouse && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                      {errors.hasSpouse.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Spouse Fields - Only if has spouse */}
              {hasSpouse && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <FormControl error={!!errors.spouseGender}>
                      <FormLabel>Spouse gender</FormLabel>
                      <Controller
                        name="spouseGender"
                        control={control}
                        render={({ field }) => (
                          <ToggleButtonGroup
                            {...field}
                            exclusive
                            onChange={(_, value) => value && field.onChange(value)}
                            sx={{ mt: 1 }}
                          >
                            <ToggleButton value="male">Male</ToggleButton>
                            <ToggleButton value="female">Female</ToggleButton>
                          </ToggleButtonGroup>
                        )}
                      />
                      {errors.spouseGender && (
                        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                          {errors.spouseGender.message}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 8 }}>
                    <FormControl fullWidth>
                      <FormLabel>
                        {investorType === 'existing'
                          ? 'Spouse age at policy commencement'
                          : 'Spouse age'}
                      </FormLabel>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                        <Controller
                          name="spouseAge"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              value={field.value}
                              type="text"
                              sx={{ width: 100 }}
                              size="small"
                              error={!!errors.spouseAge}
                              onChange={(e) => {
                                const cleaned = cleanUpNumber(e.target.value);
                                if (cleaned) field.onChange(cleaned ? Number(cleaned) : '');
                              }}
                              onBlur={field.onBlur}
                              name={field.name}
                              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            />
                          )}
                        />
                        <Box sx={{ flex: 1, px: 2 }}>
                          <Controller
                            name="spouseAge"
                            control={control}
                            render={({ field }) => (
                              <Slider 
                                {...field} 
                                min={validationValues.spouseAgeMin}
                                max={validationValues.spouseAgeMax}
                                step={1} 
                                marks 
                                valueLabelDisplay="auto" 
                                slotProps={{
                                  valueLabel: {
                                    style: {
                                      transition: 'opacity 1s ease-in-out',
                                      opacity: 0,
                                    },
                                  },
                                }}
                                onChangeCommitted={() => {
                                  const valueLabel = document.querySelector('.MuiSlider-valueLabel') as HTMLElement;
                                  if (valueLabel) {
                                    valueLabel.style.opacity = '1';
                                    setTimeout(() => {
                                      valueLabel.style.opacity = '0';
                                    }, 2000);
                                  }
                                }}
                                onChange={(_, value) => {
                                  field.onChange(value);
                                  const valueLabel = document.querySelector('.MuiSlider-valueLabel') as HTMLElement;
                                  if (valueLabel) {
                                    valueLabel.style.opacity = '1';
                                  }
                                }}
                              />
                            )}
                          />
                        </Box>
                      </Box>
                      {errors.spouseAge && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                          {errors.spouseAge.message}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Younger Spouse - Only if ages are equal */}
                  {showYoungerSpouseField && (
                    <Grid size={{ xs: 12 }}>
                      <FormControl error={!!errors.youngerSpouse}>
                        <FormLabel>Who is younger?</FormLabel>
                        <Controller
                          name="youngerSpouse"
                          control={control}
                          render={({ field }) => (
                            <ToggleButtonGroup
                              {...field}
                              exclusive
                              onChange={(_, value) => value && field.onChange(value)}
                              sx={{ mt: 1 }}
                            >
                              <ToggleButton value="lifeInsured">Life Insured</ToggleButton>
                              <ToggleButton value="spouse">Spouse</ToggleButton>
                            </ToggleButtonGroup>
                          )}
                        />
                        {errors.youngerSpouse && (
                          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                            {errors.youngerSpouse.message}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </>
              )}

              {/* Age Pension + */}
              <Grid size={{ xs: 12 }}>
                <FormControl error={!!errors.agePensionPlus} disabled={disableSpouseAndPension || false}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormLabel>Elect Age Pension+ Option?</FormLabel>
                    <InfoTooltip>
                      Election of the 'Age Pension+' option may qualify the investor for additional Age Pension due to the AGILE investment being subject to a reduced Age Pension assets test. Election of this option will cap the amount accessible upon death or withdrawal at legislated maximums commonly referred to as the social security Capital Access Schedule; and may vary the Lifetime Income Rates used to calculate the Lifetime income amount. Election is irrevocable. Refer to PDS for more information.
                    </InfoTooltip>
                  </Box>
                  <Controller
                    name="agePensionPlus"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        {...field}
                        row
                        value={field.value?.toString() || ''}
                        onChange={(e) => field.onChange(e.target.value === 'true')}
                        sx={{ mt: 1 }}
                      >
                        <FormControlLabel value="true" control={<Radio />} label="Yes" disabled={disableSpouseAndPension || false} />
                        <FormControlLabel value="false" control={<Radio />} label="No" disabled={disableSpouseAndPension || false} />
                      </RadioGroup>
                    )}
                  />
                  {errors.agePensionPlus && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                      {errors.agePensionPlus.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Income Payment Options */}
              <Grid size={{ xs: 12 }}>
                <FormControl error={!!errors.paymentType}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormLabel>Income payment option</FormLabel>
                    <InfoTooltip>
                      <p>A choice between:</p>
                      <p>Lifetime Income – Rising option; - a guaranteed income that will rise with increases in the Annual Return of the Protected Investment Option but will not reduce if there is a fall in the market; or</p>
                      <p>Lifetime Income – Fixed option; - the guaranteed income will be a constant amount over time.</p>
                      <p>The starting lifetime income percentages for Lifetime Income – Fixed option will begin higher than Lifetime Income – Rising option.</p>
                    </InfoTooltip>
                  </Box>
                  <Controller
                    name="paymentType"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field} row sx={{ mt: 1 }} value={field.value || ''}>
                        <FormControlLabel value="fixed" control={<Radio />} label="Fixed" />
                        <FormControlLabel value="rising" control={<Radio />} label="Rising" />
                      </RadioGroup>
                    )}
                  />
                  {errors.paymentType && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                      {errors.paymentType.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Years before starting AGILE Lifetime Income - Only show when data is available */}
              <Grid size={{ xs: 12, md: 8 }}>
                <FormControl fullWidth disabled={!enableDeferralPeriod}>
                  <FormLabel>Years before starting AGILE Lifetime Income</FormLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                    <Controller
                      name="deferralPeriod"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          value={field.value}
                          type="text"
                          sx={{ width: 100 }}
                          disabled={!enableDeferralPeriod}
                          size="small"
                          error={!!errors.deferralPeriod}
                          onChange={(e) => {
                            const cleaned = cleanUpNumber(e.target.value);
                            if (cleaned) field.onChange(cleaned ? Number(cleaned) : '');
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        />
                      )}
                    />
                    <Box sx={{ flex: 1, px: 2 }}>
                      <Controller
                        name="deferralPeriod"
                        control={control}
                        render={({ field }) => (
                          <Slider 
                            {...field} 
                            min={deferralPeriodMin}
                            max={deferralPeriodMax}
                            step={1} 
                            disabled={!enableDeferralPeriod}
                            marks 
                            valueLabelDisplay="auto" 
                            slotProps={{
                              valueLabel: {
                                style: {
                                  transition: 'opacity 1s ease-in-out',
                                  opacity: 0,
                                },
                              },
                            }}
                            onChangeCommitted={() => {
                              const valueLabel = document.querySelector('.MuiSlider-valueLabel') as HTMLElement;
                              if (valueLabel) {
                                valueLabel.style.opacity = '1';
                                setTimeout(() => {
                                  valueLabel.style.opacity = '0';
                                }, 2000);
                              }
                            }}
                            onChange={(_, value) => {
                              field.onChange(value);
                              const valueLabel = document.querySelector('.MuiSlider-valueLabel') as HTMLElement;
                              if (valueLabel) {
                                valueLabel.style.opacity = '1';
                              }
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  {errors.deferralPeriod && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {errors.deferralPeriod.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Calculate Button */}
              <Grid size={{ xs: 12 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  {loading ? 'getting rates...' : 'Lookup Rates'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </LocalizationProvider>
  );
};

export default RateInputForm;
